import { useEffect, useState } from 'react';
import { pagesPubliquesApi } from '../../api/resources';
import { activeLaTraduction } from '../../lib/traduction-ecran';
import { C } from '../../theme/palette';

/**
 * Le bouton « 📄 Ouvrir l'offre n° 47 en PDF » de l'e-mail d'envoi mène ici.
 *
 * <p>« Si quelqu'un appuie dessus et ouvre le PDF ou le télécharge, je veux
 * qu'on me signale. » Un PDF joint à un e-mail ne prévient jamais personne :
 * il ne part donc plus joint. Le bouton de l'e-mail ouvre cette page, qui
 * vérifie le lien puis passe aussitôt au PDF lui-même ; le serveur note
 * l'ouverture, et la cloche du bureau sonne. Le client voit son PDF comme
 * une pièce jointe : il l'enregistre ou l'imprime depuis son téléphone.
 *
 * <p>La page passe par le navigateur avant le PDF : les messageries qui
 * vérifient les liens d'un e-mail (sans l'ouvrir) ne font pas sonner la cloche.
 *
 * @param {{jeton: string}} props jeton du lien de la pièce
 * @returns {import('react').ReactElement} la page d'attente
 */
export function OuverturePdf({ jeton }) {
  const [introuvable, setIntrouvable] = useState(false);
  const adresse = pagesPubliquesApi.adresseDuPdfCourriel(jeton);

  useEffect(() => {
    let annule = false;
    Promise.resolve()
      .then(() => pagesPubliquesApi.visiteur(jeton))
      .then(() => {
        if (!annule) window.location.replace(adresse);
      })
      .catch(() => {
        if (!annule) setIntrouvable(true);
      });
    return () => {
      annule = true;
    };
  }, [jeton, adresse]);

  // Le lien ne s'ouvre plus : le message se lit dans la langue du téléphone.
  // L'attente, elle, ne dure qu'un instant et ne charge pas de dictionnaire.
  useEffect(() => {
    if (!introuvable) return;
    let langue = 'fr';
    try {
      const l = String(navigator.language || 'fr').toLowerCase();
      langue = /^zh-(tw|hk|mo|hant)/.test(l) ? 'zh-tw' : l.split('-')[0];
    } catch {
      langue = 'fr';
    }
    activeLaTraduction(langue).catch(() => {});
  }, [introuvable]);

  const page = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    boxSizing: 'border-box',
    background: '#f5f7fa',
    fontFamily: '-apple-system, Segoe UI, Helvetica, Arial, sans-serif',
  };
  const carte = {
    maxWidth: 380,
    width: '100%',
    background: '#fff',
    borderRadius: 16,
    padding: 24,
    textAlign: 'center',
    boxShadow: '0 4px 18px rgba(0,0,0,.08)',
  };

  if (introuvable) {
    return (
      <div style={page}>
        <div style={carte} role="alert">
          <div style={{ fontSize: 34, marginBottom: 8 }}>📄</div>
          <div style={{ fontSize: 17, fontWeight: 800, color: C.navy, marginBottom: 8 }}>
            Ce lien ne s’ouvre plus
          </div>
          <div style={{ fontSize: 14, color: C.grey, lineHeight: 1.5 }}>
            Le document n’est plus disponible à cette adresse. Demandez-en un nouveau à l’entreprise
            qui vous l’a envoyé.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={page}>
      <div style={carte} role="status">
        <div style={{ fontSize: 34, marginBottom: 8 }}>📄</div>
        <div style={{ fontSize: 17, fontWeight: 800, color: C.navy, marginBottom: 14 }}>
          Ouverture du PDF…
        </div>
        <a
          href={adresse}
          style={{
            display: 'block',
            background: C.navy,
            color: '#fff',
            fontWeight: 800,
            fontSize: 15,
            borderRadius: 12,
            padding: '14px 16px',
            textDecoration: 'none',
            marginBottom: 10,
          }}
        >
          📄 Ouvrir le PDF
        </a>
        <a
          href={`/p/${encodeURIComponent(jeton)}`}
          style={{ fontSize: 13, color: C.teal, fontWeight: 700, textDecoration: 'none' }}
        >
          Voir le document en ligne
        </a>
      </div>
    </div>
  );
}
